//* 组件库
import type { UiTypes } from "various-ui";
//* 公共组件
import type { DefaultSearch } from "@/components/controls";
//* 组件属性
import type { ModuleData, ModuleGeneData, ModuleMutationData } from "../index";

export const useComposable = () => {
    //* 插件
    const i18n = useI18n();
    //* 基因检索数据
    const data = reactive<ModuleData>({
        search: { id: "", mode: "Gene", load: false, name: "", symbol: "", status: "", keyword: "", transcript: "", transcripts: [] },
        sequence: { id: "", mode: "Gene", load: false, name: "", symbol: "", status: "", keyword: "", transcript: "", transcripts: [] },
        mutation: { id: "", mode: "Mutation", load: false, name: "", symbol: "", status: "", keyword: "", transcript: "", transcripts: [] },
    });

    //* 节点
    const nodes = {
        search_input: ref<InstanceType<typeof DefaultSearch>>(),
        sequence_input: ref<InstanceType<typeof DefaultSearch>>(),
        mutation_input: ref<InstanceType<typeof DefaultSearch>>(),
    };

    //* 函数列表
    const methods = {
        //* 数据重置
        reset: (data: ModuleGeneData | ModuleMutationData, input?: InstanceType<typeof DefaultSearch>) => {
            data.id = "";
            data.name = "";
            data.symbol = "";
            data.status = "";
            data.keyword = "";
            data.response = undefined;
            data.transcript = "";
            data.transcripts = [];
            input?.reset();
        },

        //* 基因检索
        change: (data: ModuleGeneData | ModuleMutationData, response?: UiTypes.candidate) => {
            data.load = true;
            api.frontend("/api/gene/mrnas-info", {
                data: { id: response?.id },
                hidden: true,
                finally: () => ((data.load = false), (data.id = response?.id), (data.name = response?.value), (data.symbol = response?.value)),
                success: (response) => {
                    //* 数据更新
                    data.transcripts = response.data || [];
                    data.transcript = data.transcripts?.[0].value;
                    //* 触发转录本切换回调
                    methods.transcript(data);
                },
            });
        },

        //* 突变检索
        search: (data: ModuleMutationData) => {
            const keyword = data.keyword.trim();
            if (!keyword) (data.status = "error"), (data.response = undefined);
            else {
                //* 开启加载器
                data.load = true;
                //* 检测突变信息是否合法
                api.frontend("/api/mutations/analyze2", {
                    data: { id: data.id, keyword, transcript: data.transcript },
                    hidden: true,
                    method: "POST",
                    error: () => ((data.status = "error"), (data.response = undefined)),
                    finally: () => (data.load = false),
                    success: (response) => {
                        if (!response.site) (data.status = "error"), (data.response = undefined);
                        else {
                            data.status = "success";
                            data.response = Object.assign(response, { keyword, name: data.symbol, transcripts: data.transcripts });
                        }
                    },
                });
            }
        },

        //* 数据获取
        obtain: (response: ModuleGeneData | ModuleMutationData, origin: ModuleGeneData, input?: InstanceType<typeof DefaultSearch>) => {
            if (!origin.response) return;
            else {
                //* 数据更新
                response.id = origin.response.id;
                response.name = origin.response.name;
                response.symbol = origin.response.name;
                response.status = origin.status;
                response.response = origin.response;
                response.transcript = origin.response.transcript;
                response.transcripts = origin.response.transcripts;
                if (response.mode == "Mutation") {
                    response.keyword = "";
                    response.response = undefined;
                }

                //* 重置输入框内容
                input?.reset({ keyword: response.name });
            }
        },

        //* 数据确认
        confirm: (data: ModuleGeneData) => {
            data.status = "success";
            data.response = {
                id: data.id,
                name: data.symbol,
                transcript: data.transcript,
                transcripts: data.transcripts,
            };
        },

        //* 切换转录本
        transcript: (data: ModuleGeneData | ModuleMutationData) => {
            data.response = undefined;
            data.keyword = "";
            data.status = "";
        },
    };

    return { data, nodes, methods };
};
